import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ComponentBase } from '../_component.base.js';
import { JnjLogoutSuccessToastComponent } from './components/jnj-logout-success-toast/jnj-logout-success-toast.component.js';
import {
	ApplicationContext,
	AuthnService,
	getUrlFromRoute,
	UserService,
	WebAnalyticsService,
} from '@Common';
import { TokenService } from '../../services/token/token.service.js';
import { DeploymentContext } from '../../utilities/deployment-context/deployment-context.js';
import { AuthnSource, LocalizedTextIds } from 'company-finder-common';
import { Location } from '@angular/common';

@Component({
	selector: 'log-in-out-banner',
	templateUrl: './log-in-out-banner.component.html',
	styleUrls: ['./log-in-out-banner.component.scss'],
	standalone: false,
})
export class LogInOutBannerComponent extends ComponentBase {
	/** See documentation where this is referenced */
	public constructor(
		dc: DeploymentContext,
		private applicationContext: ApplicationContext,
		private authnService: AuthnService,
		private router: Router,
		private toastrService: ToastrService,
		private tokenService: TokenService,
		private userService: UserService,
		private webAnalyticsService: WebAnalyticsService,
		private location: Location
	) {
		super(dc);
	}

	public get jnjEverLoggedIn(): boolean {
		return !!this.applicationContext.jnjEverLoggedIn;
	}

	public get isInternal(): boolean {
		return this.authnService.isInternal;
	}

	public get name(): string {
		return this.userService.getCachedUserSync()?.nickname;
	}

	public get showBanner(): boolean {
		return (
			this.isInternal ||
			(!this.authnService.isAuthenticated && this.jnjEverLoggedIn)
		);
	}

	public get currentPageUrl(): string {
		return encodeURIComponent(this.location.path());
	}

	public login(): void {
		// You generally shouldn't have an auth token if the login option is visible
		// But clearing handles a potential Company User bypass login token for a user with "jnjEverLoggedIn = true"
		// Note that if they are logged into JUniverse, they will likely just be automatically reauthenticated against that.
		this.clearTokenAndUser();
		this.router.navigateByUrl(
			`${this._deploymentContext.loginRoute}?returnUrl=${this.currentPageUrl}`
		);
	}

	public logout(): void {
		const userBeingLoggedOut = this.authnService.loginInfo;

		this.clearTokenAndUser();
		if (userBeingLoggedOut?.origin !== AuthnSource.JUniverse) {
			this.tokenService.setLoggedOutFlags(true);
		}
		this.router.navigate(['/']);
		const title = this.Loc(LocalizedTextIds.LogInOutBannerSuccessfully);

		this.webAnalyticsService.trackEvent('logout', {
			category: 'logout',
			label: `${userBeingLoggedOut?.id} - ${userBeingLoggedOut?.role}`,
		});

		// NOTE: Piece of the message need to be styled, so the message is
		//       hardcoded in the custom JnJLogoutSuccessToast component.
		this.toastrService.success(undefined, title, {
			positionClass: 'toast-top-right',
			tapToDismiss: true,
			timeOut: this._deploymentContext.logoutSuccessToastTimeout,
			toastComponent: JnjLogoutSuccessToastComponent,
		});
	}

	private clearTokenAndUser(): void {
		this.authnService.clearToken();
		this.authnService.clearJnjWasLoggedIn();
		this.userService.clearCachedUser();
	}
}
